import { Link, router } from "@inertiajs/react";
import type Booze from "@/types/serializers/Booze";
import type Producer from "@/types/serializers/Producer";
import { useFlashToast } from "@/hooks/useFlashToast";
import { useIndexNavigation, useSyncedFilter } from "@/hooks/useIndexNavigation";
import { ProducerFilterCombobox } from "@/components/ProducerFilterCombobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
  X,
} from "lucide-react";

type Props = {
  boozes: Booze[];
  producers: Producer[];
  total: number;
  page: { size: number; number: number };
  totalPages: number;
  flash?: { notice?: string; alert?: string };
};

function SortIcon({ direction }: { direction: "asc" | "desc" | null }) {
  if (direction === "asc") return <ArrowUp className="size-[11.2px]" />;
  if (direction === "desc") return <ArrowDown className="size-[11.2px]" />;
  return <ArrowUpDown className="size-[11.2px] text-muted-foreground" />;
}

export default function BoozesIndex({
  boozes,
  producers,
  total,
  page,
  totalPages,
  flash,
}: Props) {
  useFlashToast(flash);
  const navigation = useIndexNavigation("/boozes", {
    only: ["boozes", "total", "totalPages", "page"],
  });
  const { params, navigate, handleSort, sortDirection, pagination, queryString } = navigation;
  const [filterName, setFilterName] = useSyncedFilter("filter_name", navigation);

  function handleDelete(id: number) {
    if (!confirm("Are you sure you want to delete this booze entry?")) return;
    router.delete(`/boozes/${id}${queryString}`);
  }

  function clearFilters() {
    navigate({
      filter_name: null,
      filter_producer_id: null,
      offset: null,
    });
  }

  const filterProducerId = params.filter_producer_id ?? "";
  const hasFilters = !!filterName || !!filterProducerId;
  const { currentPage, canPrev, canNext } = pagination(page, totalPages);

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Booze</h1>
          <p className="text-sm text-muted-foreground">
            {total} {total === 1 ? "entry" : "entries"} total
          </p>
        </div>
        <Button asChild>
          <Link href="/boozes/new">
            <Plus className="size-4" />
            New Booze
          </Link>
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Input
            placeholder="Filter by name…"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            className="pl-[40px]"
            style={{ paddingLeft: "30px" }}
          />
          <Search className="pointer-events-none absolute left-2.5 top-0 h-9 size-[16.7px] text-muted-foreground" />
        </div>
        <ProducerFilterCombobox
          producers={producers}
          selectedId={filterProducerId}
          onSelect={(id) =>
            navigate({ filter_producer_id: id, offset: null })
          }
        />
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="size-4" />
            Clear
          </Button>
        )}
      </div>

      {/* Mobile: id, name, producer, image; md+ use full table below */}
      <div className="md:hidden rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[60px]">
                <button
                  className="inline-flex items-center gap-1"
                  onClick={() => handleSort("id")}
                >
                  ID <SortIcon direction={sortDirection("id")} />
                </button>
              </TableHead>
              <TableHead>
                <button
                  className="inline-flex items-center gap-1"
                  onClick={() => handleSort("name")}
                >
                  Name <SortIcon direction={sortDirection("name")} />
                </button>
              </TableHead>
              <TableHead>Producer</TableHead>
              <TableHead className="w-[72px]">Image</TableHead>
              <TableHead className="w-[1%] min-w-0 text-right pr-1">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {boozes.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="h-24 text-center text-muted-foreground"
                >
                  No booze found.
                </TableCell>
              </TableRow>
            ) : (
              boozes.map((booze) => (
                <TableRow key={booze.id}>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {booze.id}
                  </TableCell>
                  <TableCell className="max-w-0">
                    <Link
                      href={`/boozes/${booze.id}`}
                      className="block truncate font-medium hover:underline"
                    >
                      {booze.name}
                    </Link>
                  </TableCell>
                  <TableCell className="max-w-0 text-muted-foreground text-sm">
                    <span className="block truncate">
                      {booze.producer?.name || (
                        <span className="italic">—</span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="w-[72px] p-1 align-middle text-muted-foreground">
                    {booze.imageUrl ? (
                      <img
                        src={booze.imageUrl as string}
                        alt=""
                        width={64}
                        height={64}
                        className="size-16 rounded object-cover"
                      />
                    ) : (
                      <span className="text-xs italic">—</span>
                    )}
                  </TableCell>
                  <TableCell className="p-1 text-right align-top">
                    <div className="flex flex-col items-stretch gap-0.5">
                      <Button variant="ghost" size="sm" className="h-8 px-2" asChild>
                        <Link href={`/boozes/${booze.id}`}>View</Link>
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 px-2" asChild>
                        <Link href={`/boozes/${booze.id}/edit`}>Edit</Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-destructive hover:text-destructive"
                        onClick={() => handleDelete(booze.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="hidden md:block rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[60px]">
                <button
                  className="inline-flex items-center gap-1"
                  onClick={() => handleSort("id")}
                >
                  ID <SortIcon direction={sortDirection("id")} />
                </button>
              </TableHead>
              <TableHead>
                <button
                  className="inline-flex items-center gap-1"
                  onClick={() => handleSort("name")}
                >
                  Name <SortIcon direction={sortDirection("name")} />
                </button>
              </TableHead>
              <TableHead>Image</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Producer</TableHead>
              <TableHead className="hidden lg:table-cell">Country</TableHead>
              <TableHead className="hidden lg:table-cell">Size</TableHead>
              <TableHead className="w-[120px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {boozes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                  No booze found.
                </TableCell>
              </TableRow>
            ) : (
              boozes.map((booze) => (
                <TableRow key={booze.id}>
                  <TableCell className="font-mono text-muted-foreground">
                    {booze.id}
                  </TableCell>
                  <TableCell className="font-medium">
                    <Link
                      href={`/boozes/${booze.id}`}
                      className="hover:underline"
                    >
                      {booze.name}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {booze.imageUrl ? (
                      <img
                        src={booze.imageUrl as string}
                        alt={booze.name}
                        width={100}
                        height={100}
                        className="size-[100px] rounded object-cover"
                      />
                    ) : (
                      <span className="italic">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {booze.category?.name || <span className="italic">—</span>}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {booze.producer?.name || <span className="italic">—</span>}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-muted-foreground">
                    {booze.country?.name || <span className="italic">—</span>}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-muted-foreground">
                    {booze.size ? `${booze.size.volume} ${booze.size.measurementType}` : <span className="italic">—</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" asChild>
                        <Link href={`/boozes/${booze.id}`}>View</Link>
                      </Button>
                      <Button variant="ghost" size="sm" asChild>
                        <Link href={`/boozes/${booze.id}/edit`}>Edit</Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDelete(booze.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Rows per page</span>
            <Select
              value={String(page.size)}
              onValueChange={(value) =>
                navigate({ page_size: value, offset: null })
              }
            >
              <SelectTrigger className="w-[70px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 15, 25].map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                disabled={!canPrev}
                onClick={() =>
                  navigate({ offset: String(page.number - page.size) })
                }
              >
                <ChevronLeft className="size-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                disabled={!canNext}
                onClick={() =>
                  navigate({ offset: String(page.number + page.size) })
                }
              >
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
